import React from 'react'
import PropTypes from 'prop-types'
import {Flex} from '@chakra-ui/react'

import ProductRail from './ProductRail'
import NavPanel from './NavPanel'
import {readNavCollapsed, writeNavCollapsed} from './collapseStorage'
import {accountType, balanceType, productType, utilityActionType} from './types'

// SideNavV2 — the product-separated supplier navigation.
//
// The supplier's products (Payments on Demand, Points on Payments, ...) each
// own a rail icon, a panel heading and their own set of links. Selecting a
// product in the rail swaps the panel to that product's links. Below the
// product switcher sit shared utility actions; the panel pins a balance and
// the account switcher to the bottom.
const SideNavV2 = ({
  products,
  activeProductId,
  onProductChange,
  utilityActions,
  signOutAction,
  signOutIcon,
  authenticityToken,
  balance,
  account,
  comingSoonLabel,
  onCollapse,
  embedded,
  onClose,
}) => {
  const activeProduct =
    products.find((product) => product.id === activeProductId) || products[0]

  // The drawer (embedded) always opens expanded; the persisted collapsed state
  // only applies to the fixed desktop nav.
  const [collapsed, setCollapsed] = React.useState(
    embedded ? false : readNavCollapsed,
  )

  return (
    <Flex
      h="100%"
      w={embedded ? '100%' : undefined}
      position={embedded ? 'static' : 'fixed'}
      top={embedded ? undefined : '0'}
      left={embedded ? undefined : '0'}
      zIndex={embedded ? undefined : '9'}
      // The neutral.200 field the panel floats on; the rail sits directly on it
      // and a 12px gutter sets the card off from the page content on the right.
      bg={embedded ? undefined : 'mld.neutral.200'}
      pr={embedded ? undefined : '12px'}
    >
      <ProductRail
        products={products}
        activeProductId={activeProduct?.id}
        onProductChange={onProductChange}
        utilityActions={utilityActions}
        signOutAction={signOutAction}
        signOutIcon={signOutIcon}
        authenticityToken={authenticityToken}
      />
      {activeProduct && (
        <NavPanel
          title={activeProduct.name}
          showBrand={products.length <= 1}
          links={activeProduct.links}
          balance={balance}
          account={account}
          comingSoonLabel={comingSoonLabel}
          onClose={onClose}
          collapsed={collapsed}
          onToggleCollapse={() => {
            const next = !collapsed
            setCollapsed(next)
            writeNavCollapsed(next)
            onCollapse(next)
          }}
        />
      )}
    </Flex>
  )
}

SideNavV2.propTypes = {
  products: PropTypes.arrayOf(productType).isRequired,
  activeProductId: PropTypes.string,
  onProductChange: PropTypes.func,
  utilityActions: PropTypes.arrayOf(utilityActionType),
  signOutAction: PropTypes.string,
  signOutIcon: PropTypes.node,
  authenticityToken: PropTypes.string,
  balance: balanceType,
  account: accountType,
  comingSoonLabel: PropTypes.string,
  onCollapse: PropTypes.func,
  // Renders without fixed positioning, for use inside the mobile drawer.
  embedded: PropTypes.bool,
  // When set (mobile drawer), the panel shows a close button instead of the
  // collapse toggle and fills the available width.
  onClose: PropTypes.func,
}

SideNavV2.defaultProps = {
  activeProductId: undefined,
  onProductChange: () => {},
  utilityActions: [],
  signOutAction: undefined,
  signOutIcon: null,
  authenticityToken: '',
  balance: undefined,
  account: undefined,
  comingSoonLabel: 'Coming soon',
  onCollapse: () => {},
  embedded: false,
  onClose: undefined,
}

export default SideNavV2
